import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RoleFindInterface } from '@zaoblako/shared/modules/security/dtos/role/role-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserFormDto, UserFormInterface } from '@zaoblako/shared/modules/security/dtos/user/user-form.dto';
import type { WorkScheduleFindInterface } from '@zaoblako/shared/modules/security/dtos/work-schedule/work-schedule-find.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { RoleService } from '@zaoblako/web/core/services/security/role.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { WorkScheduleService } from '@zaoblako/web/core/services/security/work-schedule.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { StandardMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-multi-select/standard-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputMaskModule } from 'primeng/inputmask';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-user-edit-form',
  templateUrl: './user-edit-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonComponent,
    InputMaskModule,
    FormsModule,
    StandardMultiSelectComponent,
    SingleSelectComponent,
    SpinnerComponent,
    ContragentAutoCompleteComponent,
    ApprovalSchemeSingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserEditFormComponent extends BaseFormComponent<UserFormInterface> implements OnInit {
  public readonly roleDropdownDescriptorUrl: string;

  @Input() public readonly index: number;
  public user: UserFormInterface = <UserFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public dto = UserFormDto;

  public roles: RoleFindInterface[] = [];
  public schedules: WorkScheduleFindInterface[] = [];

  public constructor(
    roleService: RoleService,
    private readonly userService: UserService,
    private readonly formStoreService: FormStoreService,
    private readonly workScheduleService: WorkScheduleService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
    this.roleDropdownDescriptorUrl = roleService.dropdownDescriptorUrl;
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.workScheduleService
      .find<WorkScheduleFindInterface>()
      .subscribe((schedules) => {
        this.schedules = schedules;
        this.cdRef.markForCheck();
      });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.userService.findById<UserFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
      next: (user) => {
        this.user = user as any;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveUser() {
    if (this.checkValidate(this.user).valid) {
      this.formStoreService.setLoading(true, this.index);

      this.userService.update<UserFormInterface, UserFindInterface>(this.user).subscribe({
        next: (user) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Пользователь "${user.name}" обновлен`);
          this.formStoreService.saveNext(user, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }
}
